import { useNavigate } from '@tanstack/react-router';
import { CalendarPlus, ChevronLeft, ChevronRight, ClipboardCheck, Download, FastForward, Mail, MoreHorizontal, Rocket, Tag, UserCheck, UserPlus, UsersRound, XCircle } from 'lucide-react';
import { ComposePanel } from '../../communication/components/ComposePanel';
import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  BULK_MAX_RECORDS,
  BULK_TOO_MANY,
  INTERVIEW_STAGES,
  PIPELINE_COLUMNS,
  SORTING_QUEUE_STAGES,
  nextStageOf,
  sortOf,
  sortToString,
  type ApplicationStage,
  type BulkActionRequest,
  type PipelineColumn,
  type PipelineListResponse,
  type PipelineOptions,
  type PipelineRow,
  type PipelineSelection,
  type ScreeningGuard,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DataTable } from '../../../components/data-table/DataTable';
import { STAGE_TONES } from '../../../components/status-tones';
import type { Column, SortSpec } from '../../../components/data-table/types';
import type { TablePreferences } from '../../../components/data-table/use-table-preferences';
import { MenuGroup, MenuItem, Popover } from '../../../components/Popover';
import { EmptyState, FilteredEmptyState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, isSelected, resolveSelection, selectionCount, type SelectionState } from '../../../lib/selection';
import { useToasts } from '../../../stores/toasts';
import { BookSessionPanel } from '../../assessments/components/BookSessionPanel';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { AddToCvBankButton } from '../../cv-bank/components/AddToCvBankButton';
import { downloadExport } from '../api/pipeline-api';
import type { RowPatch, usePipelineActions } from '../hooks/use-pipeline-actions';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER, intentFor, reasonOptions } from '../move-intent';
import type { PipelineSearch } from '../pipeline-search';
import { ChoosePicker, type Choice } from './ChoosePicker';
import { useRowMoves } from './RowMoves';
import styles from './PipelineList.module.css';
import { useModules } from '../../../lib/modules';

const QUEUE: readonly ApplicationStage[] = SORTING_QUEUE_STAGES;

/** The bulk bar's pickers; one application's moves are `useRowMoves`, shared with the board. */
type BulkPopped = { readonly kind: 'reject' | 'pool' | 'tag' | 'assign' | 'test' | 'exam' | 'message'; readonly anchor: AnchorRect };

interface PipelineListProps {
  readonly jobId: string;
  readonly fileName: string;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
  readonly data: PipelineListResponse | undefined;
  readonly isLoading: boolean;
  readonly options: PipelineOptions | undefined;
  readonly owners: readonly { readonly id: string; readonly fullName: string }[];
  /** S17: the test the job sends; null when it has none, so there is nothing to assign. */
  readonly testName: string | null;
  /** S18 Q1: the test is sat on paper, so people are booked on sessions, not sent links. */
  readonly isPaperTest: boolean;
  readonly actions: ReturnType<typeof usePipelineActions>;
  readonly columns: readonly Column<PipelineRow>[];
  readonly preferences: TablePreferences;
  readonly filterCount: number;
  readonly onClearFilters: () => void;
  /** A JOB-12 stage (R2): the stages the list is locked to, comma-separated. */
  readonly lockedStage?: string | undefined;
}

/**
 * SCR-02 — every application on the job in the S2 data table: sortable (shift for a
 * second column), columns chosen per person, ticked with shift for ranges, and "all N
 * matching" for the whole filter (06-qol §3). Hovering a row offers the commonest
 * moves; ticking rows offers §L9's bulk actions.
 */
export function PipelineList(props: PipelineListProps): ReactNode {
  const { jobId, data, actions, options, search } = props;
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [popped, setPopped] = useState<BulkPopped | null>(null);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const can = data?.can;
  // R1 (§L34): bulk actions of switched-off modules are not offered.
  const modules = useModules();
  const activeStages = data?.job.activeStages ?? [];
  const active = new Set(activeStages);
  const rowMoves = useRowMoves({ jobId, activeStages, can, options, actions, via: 'list' });
  const canSkip = can?.skip === true;
  // D91: the screening-order guard, on by default (Module switches).
  const isScreeningGuardOn = modules.screening_order;
  const guardFor = (row: PipelineRow): ScreeningGuard => ({ isOn: isScreeningGuardOn, managerResponse: row.managerResponse });
  const page = search.page ?? 1;
  const pageCount = Math.max(1, Math.ceil(total / (data?.pageSize ?? 1)));
  const count = selectionCount(selection, total) ?? 0;
  const chosenRows = rows.filter((row) => isSelected(selection, row.id));
  const close = (): void => setPopped(null);
  // S21 Q4: an interview round is decided on its debrief (the server refuses the move anyway).
  const isAdvanceOffered = !isInterviewLock(props.lockedStage);

  const sort: readonly SortSpec[] = sortOf(search.sort).map((spec) => ({ columnId: spec.column, direction: spec.direction }));
  function changeSort(next: readonly SortSpec[]): void {
    const { page: _page, sort: _sort, ...rest } = search;
    props.onSearch(next.length === 0 ? rest : { ...rest, sort: sortToString(sortOf(next.map((spec) => `${spec.columnId}:${spec.direction}`).join(','))) });
  }

  function filterOnly(): PipelineSearch {
    const { page: _page, layout: _layout, view: _view, action: _action, ...filter } = search;
    return filter;
  }

  function selectionBody(): PipelineSelection {
    const resolved = resolveSelection(selection);
    return resolved.kind === 'ids' ? { kind: 'ids', ids: [...resolved.ids] } : { kind: 'all-matching', filter: filterOnly(), excluded: [...resolved.excluded] };
  }

  function runBulk(body: BulkActionRequest, patch: (row: PipelineRow) => RowPatch | null): void {
    close();
    void actions.runBulk(body, chosenRows, patch).then(() => setSelection(EMPTY_SELECTION));
  }

  function rowActions(row: PipelineRow): ReactNode {
    if (can?.move !== true) return null;
    const next = nextStageOf(row.stage, active);
    const advanceIntent = next === null ? null : intentFor(row.stage, next, active, canSkip, guardFor(row)).kind;
    const canAdvance = advanceIntent === 'move' || advanceIntent === 'skip_screening';
    const canReject = intentFor(row.stage, 'rejected', active, canSkip, guardFor(row)).kind === 'reject';
    return (
      <>
        {next && canAdvance && (
          <Button variant="quiet" size="sm" icon={FastForward} onClick={(event: MouseEvent<HTMLButtonElement>) => rowMoves.request(row, next, anchorFrom(event.currentTarget))}>
            Advance
          </Button>
        )}
        {canReject && (
          <Button variant="quiet" size="sm" icon={XCircle} onClick={(event: MouseEvent<HTMLButtonElement>) => rowMoves.request(row, 'rejected', anchorFrom(event.currentTarget))}>
            Reject
          </Button>
        )}
        <Button variant="ghost" size="sm" icon={MoreHorizontal} aria-label={`More for ${row.candidate.fullName}`} onClick={(event: MouseEvent<HTMLButtonElement>) => rowMoves.openMenu(row, anchorFrom(event.currentTarget))} />
      </>
    );
  }

  /** §L9 export: what is ticked, or — with nothing ticked — everything the filter shows. */
  async function exportRows(): Promise<void> {
    const columns = props.columns.filter((column) => !props.preferences.hiddenColumns.has(column.id)).map((column) => column.id).filter(isColumnId);
    try {
      await downloadExport(jobId, { selection: selection.mode === 'none' ? { kind: 'all-matching', filter: filterOnly(), excluded: [] } : selectionBody(), columns }, props.fileName);
      show({ tone: 'success', message: 'Export ready', detail: 'Fields you cannot see are in the file as —.' });
    } catch (error) {
      show({ tone: 'danger', message: "Couldn't export", detail: errorMessage(error) });
    }
  }

  const tooMany = count > BULK_MAX_RECORDS;
  const sortable = chosenRows.filter((row) => QUEUE.includes(row.stage));
  const open = (kind: BulkPopped['kind']) => (event: MouseEvent<HTMLButtonElement>) => setPopped({ kind, anchor: anchorFrom(event.currentTarget) });
  const exportButton = (
    <Button variant="quiet" size="sm" icon={Download} disabled={tooMany} onClick={() => void exportRows()}>
      Export
    </Button>
  );

  const bulkActions =
    can?.move === true ? (
      <>
        {tooMany && <span className={styles.tooMany}>{BULK_TOO_MANY}</span>}
        {isAdvanceOffered && (
          <Button variant="quiet" size="sm" icon={FastForward} disabled={tooMany} onClick={() => runBulk({ action: 'advance', selection: selectionBody() }, (row) => nextPatch(row, active, guardFor(row)))}>
            Advance
          </Button>
        )}
        <Button variant="quiet" size="sm" icon={XCircle} disabled={tooMany} onClick={open('reject')}>
          Reject
        </Button>
        {can.pool && modules.pools && (
          <Button variant="quiet" size="sm" icon={UsersRound} disabled={tooMany} onClick={open('pool')}>
            Add to pool
          </Button>
        )}
        <Button variant="quiet" size="sm" icon={Tag} disabled={tooMany} onClick={open('tag')}>
          Tag
        </Button>
        <Button variant="quiet" size="sm" icon={UserCheck} disabled={tooMany} onClick={open('assign')}>
          Assign
        </Button>
        <Button variant="quiet" size="sm" icon={Mail} disabled={tooMany} onClick={open('message')}>
          Send message
        </Button>
        {modules.assessments && props.testName !== null && !props.isPaperTest && (
          <Button variant="quiet" size="sm" icon={ClipboardCheck} disabled={tooMany} onClick={open('test')}>
            Assign assessment
          </Button>
        )}
        {modules.exam_sessions && props.testName !== null && props.isPaperTest && (
          <Button variant="quiet" size="sm" icon={CalendarPlus} disabled={tooMany} onClick={open('exam')}>
            Add to exam session
          </Button>
        )}
        {exportButton}
        {selection.mode === 'some' && <AddToCvBankButton people={chosenRows.map((row) => ({ candidateId: row.candidate.id, name: row.candidate.fullName }))} onDone={() => setSelection(EMPTY_SELECTION)} />}
        {sortable.length > 0 && selection.mode === 'some' && (
          <Button variant="quiet" size="sm" icon={Rocket} onClick={() => void navigate({ to: '/jobs/$jobId/sort', params: { jobId }, state: { sortOnly: sortable.map((row) => row.id) } })}>
            Sort {formatNumber(sortable.length)}
          </Button>
        )}
      </>
    ) : (
      exportButton
    );

  const people = `${formatNumber(count)} ${count === 1 ? 'person' : 'people'}`;

  return (
    <>
      <DataTable
        screenId="pipeline-list"
        label="Pipeline"
        columns={props.columns}
        rows={rows}
        toneOf={(row) => STAGE_TONES[row.stage]}
        getRowId={(row) => row.id}
        total={total}
        sort={sort}
        onSortChange={changeSort}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) => void navigate({ to: '/applications/$applicationId', params: { applicationId: row.id } })}
        rowActions={rowActions}
        bulkActions={bulkActions}
        preferences={props.preferences}
        isLoading={props.isLoading}
        emptyState={
          props.filterCount > 0 ? (
            <FilteredEmptyState filterCount={props.filterCount} onClearFilters={props.onClearFilters} />
          ) : (
            <EmptyState
              title="Nobody here yet"
              body="Applications appear as people apply through the job's link. You can also add someone yourself."
              action={
                <Button variant="solid" icon={UserPlus} onClick={() => void navigate({ to: '/candidates/new' })}>
                  Add candidate
                </Button>
              }
            />
          )
        }
      />

      {pageCount > 1 && (
        <nav className={styles.pager} aria-label="Pages">
          <Button variant="ghost" icon={ChevronLeft} disabled={page <= 1} onClick={() => props.onSearch({ ...search, page: page - 1 })}>
            Previous
          </Button>
          <span className={styles.pageOf}>
            Page {page} of {pageCount}
          </span>
          <Button variant="ghost" disabled={page >= pageCount} onClick={() => props.onSearch({ ...search, page: page + 1 })}>
            Next
            <ChevronRight size={16} aria-hidden />
          </Button>
        </nav>
      )}

      {rowMoves.popover}

      <Popover anchor={popped?.anchor ?? null} onClose={close} align="right" label={popped ? `${popped.kind} ${people}` : 'Bulk action'}>
        {popped?.kind === 'reject' && (
          <ReasonPicker
            mode="reject"
            reasons={reasonOptions(options?.reasons ?? [], chosenRows.length > 0 ? chosenRows.map((row) => row.stage) : ['screening'])}
            pools={options?.pools ?? []}
            canPool={can?.pool === true}
            contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
            candidateName={people}
            canAddToCvBank
            onConfirm={(choice) => runBulk({ action: 'reject', selection: selectionBody(), reasonCode: choice.reasonCode, poolIds: [...choice.poolIds], newPoolNames: [...choice.newPoolNames], addToCvBank: choice.addToCvBank }, () => ({ stage: 'rejected' }))}
          />
        )}
        {popped?.kind === 'pool' && (
          <ChoosePicker
            title={`Add ${people} to a pool`}
            subtitle="Their applications stay where they are."
            noun="pool"
            options={options?.pools ?? []}
            confirmLabel="Add to pool"
            onConfirm={(choice: Choice) => runBulk({ action: 'pool', selection: selectionBody(), poolIds: [...choice.ids], newPoolNames: [...choice.newNames] }, () => ({ isPooled: true }))}
          />
        )}
        {popped?.kind === 'tag' && (
          <ChoosePicker
            title={`Tag ${people}`}
            subtitle="A tag stays with the person on every job."
            noun="tag"
            options={options?.tags ?? []}
            confirmLabel="Tag"
            onConfirm={(choice: Choice) => {
              // A new tag has no id yet; it is drawn by name until the refetch brings the real one.
              const drawn = [...(options?.tags ?? []).filter((tag) => choice.ids.includes(tag.id)), ...choice.newNames.map((name) => ({ id: `new:${name}`, name }))];
              runBulk({ action: 'tag', selection: selectionBody(), tagIds: [...choice.ids], newTagNames: [...choice.newNames] }, () => ({ addTags: drawn }));
            }}
          />
        )}
        {popped?.kind === 'test' && props.testName !== null && (
          <div className={styles.confirm}>
            <p className={styles.confirmTitle}>
              Send {props.testName} to {people}?
            </p>
            <p className={styles.confirmBody}>
              Anyone in Screening moves to Assessment first. Each gets an email with their own link, which you can also copy from the Assessment tab. This sends email, so it can't be undone.
            </p>
            <div className={styles.confirmActions}>
              <Button variant="ghost" size="sm" onClick={close}>
                Cancel
              </Button>
              <Button variant="solid" size="sm" onClick={() => runBulk({ action: 'assign_assessment', selection: selectionBody() }, testPatch)}>
                Send the test
              </Button>
            </div>
          </div>
        )}
        {popped?.kind === 'exam' && (
          <BookSessionPanel
            jobId={jobId}
            who={people}
            needed={count}
            onCancel={close}
            onPick={(examSessionId) => runBulk({ action: 'book_exam_session', selection: selectionBody(), examSessionId }, testPatch)}
          />
        )}
        {popped?.kind === 'message' && (
          <ComposePanel
            recipientsLabel={`${people} — each filled in with their own name and job`}
            onCancel={close}
            onDone={close}
            onSend={async (draft) => {
              // F15.03: never undoable (§L9) — nothing on the row changes, so nothing is drawn.
              runBulk({ action: 'message', selection: selectionBody(), templateKey: draft.templateKey, subject: draft.subject, body: draft.body }, () => null);
            }}
          />
        )}
        {popped?.kind === 'assign' && (
          <MenuGroup label={`Recruiter for ${people}`}>
            {props.owners.map((owner) => (
              <MenuItem key={owner.id} onClick={() => runBulk({ action: 'assign_recruiter', selection: selectionBody(), recruiterUserId: owner.id }, () => ({ recruiter: { id: owner.id, name: owner.fullName } }))}>
                {owner.fullName}
              </MenuItem>
            ))}
          </MenuGroup>
        )}
      </Popover>
    </>
  );
}

/** S17 Q4 / S18 Q2: people in the queue move to Assessment; anyone already there stays put. */
function testPatch(row: PipelineRow): RowPatch | null {
  return QUEUE.includes(row.stage) ? { stage: 'assessment' } : null;
}

function nextPatch(row: PipelineRow, active: ReadonlySet<ApplicationStage>, screeningGuard: ScreeningGuard): RowPatch | null {
  const next = nextStageOf(row.stage, active);
  // D91: a bulk advance that needs a skip reason isn't optimistic — the server decides case by case.
  return next && intentFor(row.stage, next, active, false, screeningGuard).kind === 'move' ? { stage: next } : null;
}

function isInterviewLock(lockedStage: string | undefined): boolean {
  if (lockedStage === undefined) return false;
  const interviews: readonly string[] = INTERVIEW_STAGES;
  return lockedStage.split(',').every((stage) => interviews.includes(stage));
}

function isColumnId(value: string): value is PipelineColumn {
  return (PIPELINE_COLUMNS as readonly string[]).includes(value);
}
